import { inject, onBeforeMount, onMounted, ref } from "vue";
import type { IConfigCatClient, SettingKeyValue } from "configcat-vue";
import { useAuthStore } from "@/stores/useAuthStore.ts";

export enum BooleanFeatureFlag {
  ShowLegacyGroupsPage = "showLegacyGroupsPage",
  ShowLegacyAnalyticsPage = "showLegacyAnalyticsPage",
  ShowCertificatesPage = "showCertificatesPage",
}

export const booleanFeatureFlagDefaultValue = (flag: BooleanFeatureFlag) => {
  switch (flag) {
    case BooleanFeatureFlag.ShowLegacyGroupsPage:
      return false;
    case BooleanFeatureFlag.ShowLegacyAnalyticsPage:
      return true;
    case BooleanFeatureFlag.ShowCertificatesPage:
      return false;
    default:
      throw new Error(`Unknown boolean feature flag: ${flag}`);
  }
};

export const useFeatureFlags = () => {
  const authStore = useAuthStore();

  const configCatClient =
    inject<IConfigCatClient>("configCatClient") ??
    (() => {
      throw new Error("ConfigCatPlugin was not installed.");
    })();

  const featureFlags = ref<SettingKeyValue[] | undefined>([]);

  const isBooleanFeatureFlagEnabled = async (
    flag: BooleanFeatureFlag,
    defaultValue: boolean,
  ): Promise<boolean> => {
    return await configCatClient.getValueAsync(
      flag,
      defaultValue,
      authStore.configCatUser ?? undefined,
    );
  };

  // --- Groups Page ----
  const isShowLegacyGroupsPageEnabled = ref<boolean>(
    booleanFeatureFlagDefaultValue(BooleanFeatureFlag.ShowLegacyGroupsPage),
  );
  const isShowLegacyGroupsPageInitialised = ref<boolean>(false);

  // --- Analytics Page ----
  const isShowLegacyAnalyticsPageEnabled = ref<boolean>(
    booleanFeatureFlagDefaultValue(BooleanFeatureFlag.ShowLegacyAnalyticsPage),
  );

  // --- Certificates Page ----
  const isShowCertificatesPageEnabled = ref<boolean>(
    booleanFeatureFlagDefaultValue(BooleanFeatureFlag.ShowCertificatesPage),
  );

  onBeforeMount(async () => {
    // --- Groups Page ----
    isShowLegacyGroupsPageEnabled.value = await isBooleanFeatureFlagEnabled(
      BooleanFeatureFlag.ShowLegacyGroupsPage,
      booleanFeatureFlagDefaultValue(BooleanFeatureFlag.ShowLegacyGroupsPage),
    );
    isShowLegacyGroupsPageInitialised.value = true;

    // --- Analytics Page ----
    isShowLegacyAnalyticsPageEnabled.value = await isBooleanFeatureFlagEnabled(
      BooleanFeatureFlag.ShowLegacyAnalyticsPage,
      booleanFeatureFlagDefaultValue(
        BooleanFeatureFlag.ShowLegacyAnalyticsPage,
      ),
    );

    // --- Certificates Page ----
    isShowCertificatesPageEnabled.value = await isBooleanFeatureFlagEnabled(
      BooleanFeatureFlag.ShowCertificatesPage,
      booleanFeatureFlagDefaultValue(BooleanFeatureFlag.ShowCertificatesPage),
    );
  });

  onMounted(async () => {
    featureFlags.value = await configCatClient.getAllValuesAsync(
      authStore.configCatUser ?? undefined,
    );
    console.log("All feature flags:", featureFlags.value);
  });

  return {
    featureFlags,
    isBooleanFeatureFlagEnabled,
    isShowLegacyGroupsPageEnabled,
    isShowLegacyAnalyticsPageEnabled,
    isShowCertificatesPageEnabled,
    isShowLegacyGroupsPageInitialised,
  };
};
